import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalysisFindingCard = ({ finding, onAcceptSuggestion, onCreateChangeRequest, onNavigateToDocument }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuggestionPreview, setShowSuggestionPreview] = useState(false);

  const severityConfig = {
    critical: {
      color: 'text-error',
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20',
      icon: 'AlertTriangle'
    },
    high: {
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      icon: 'AlertCircle'
    },
    medium: {
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent/20',
      icon: 'Info'
    },
    low: {
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20',
      icon: 'CheckCircle'
    }
  };

  const typeConfig = {
    gap_detection: { label: 'Gap Detection', icon: 'Search' },
    inconsistencies: { label: 'Inconsistency', icon: 'AlertTriangle' },
    contradictions: { label: 'Contradiction', icon: 'XCircle' },
    grammar_issues: { label: 'Grammar', icon: 'Type' },
    spelling_errors: { label: 'Spelling', icon: 'Spellcheck' },
    language_issues: { label: 'Language', icon: 'Globe' }
  };

  const statusConfig = {
    pending: { label: 'Pending', color: 'text-warning', bgColor: 'bg-warning/10' },
    in_review: { label: 'In Review', color: 'text-accent', bgColor: 'bg-accent/10' },
    resolved: { label: 'Resolved', color: 'text-success', bgColor: 'bg-success/10' },
    dismissed: { label: 'Dismissed', color: 'text-muted-foreground', bgColor: 'bg-muted/10' }
  };

  const severity = severityConfig?.[finding?.severity] || severityConfig?.medium;
  const type = typeConfig?.[finding?.type] || typeConfig?.gap_detection;
  const status = statusConfig?.[finding?.status] || statusConfig?.pending;

  return (
    <div className={`bg-card border rounded-lg p-6 transition-all duration-200 hover:shadow-md ${severity?.borderColor}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4 flex-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severity?.bgColor}`}>
            <Icon name={severity?.icon} size={20} className={severity?.color} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-medium text-foreground truncate">{finding?.title}</h3>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${type?.bgColor || 'bg-muted'}`}>
                  <Icon name={type?.icon} size={12} />
                  {type?.label}
                </span>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${status?.bgColor}`}>
                  <span className={status?.color}>{status?.label}</span>
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="FileText" size={14} />
                {finding?.documentName}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="MapPin" size={14} />
                {finding?.location}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                {finding?.detectedAt}
              </span>
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconSize={16}
        >
          <span className="sr-only">{isExpanded ? 'Collapse' : 'Expand'}</span>
        </Button>
      </div>
      {/* Description */}
      <div className="mb-4">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {finding?.description}
        </p>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="space-y-4 border-t border-border pt-4">
          {/* Affected Content */}
          {finding?.affectedContent && (
            <div>
              <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Quote" size={16} />
                Affected Content
              </h4>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground font-mono">
                  "{finding?.affectedContent}"
                </p>
              </div>
            </div>
          )}

          {/* AI Suggestion */}
          {finding?.suggestion && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <Icon name="Lightbulb" size={16} />
                  AI Suggestion
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSuggestionPreview(!showSuggestionPreview)}
                  iconName={showSuggestionPreview ? "EyeOff" : "Eye"}
                  iconPosition="left"
                  iconSize={14}
                >
                  {showSuggestionPreview ? 'Hide' : 'Preview'}
                </Button>
              </div>
              
              <div className="bg-success/5 border border-success/20 rounded-lg p-3">
                <p className="text-sm text-foreground">
                  {finding?.suggestion}
                </p>
              </div>

              {showSuggestionPreview && finding?.suggestedContent && (
                <div className="mt-3 bg-accent/5 border border-accent/20 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Suggested replacement:</p>
                  <p className="text-sm text-foreground font-mono">
                    "{finding?.suggestedContent}"
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Additional Details */}
          {finding?.additionalContext && (
            <div>
              <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Info" size={16} />
                Additional Context
              </h4>
              <p className="text-sm text-muted-foreground">
                {finding?.additionalContext}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onNavigateToDocument(finding?.documentId, finding?.location)}
          iconName="ExternalLink"
          iconPosition="left"
          iconSize={14}
        >
          View in Document
        </Button>

        <div className="flex items-center gap-2">
          {finding?.status === 'pending' && finding?.suggestion && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAcceptSuggestion(finding?.id)}
              iconName="Check"
              iconPosition="left"
              iconSize={14}
            >
              Accept Suggestion
            </Button>
          )}
          
          <Button
            variant="default"
            size="sm"
            onClick={() => onCreateChangeRequest(finding)}
            iconName="GitBranch"
            iconPosition="left"
            iconSize={14}
          >
            Create Change Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisFindingCard;